import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { FloppyDisk, UsersThree, DownloadSimple } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);

  if (!form) return <div className="text-slate-400">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        ...form,
        default_tax_rate: Number(form.default_tax_rate) || 0,
        invoice_start_number: Number(form.invoice_start_number) || 0,
      });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, logo_path: data.path });
      toast.success("Logo uploaded — Save to apply");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const importCsv = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      const parts = [];
      if (data.inserted) parts.push(`${data.inserted} added`);
      if (data.skipped_duplicates) parts.push(`${data.skipped_duplicates} duplicates skipped`);
      if (data.skipped_invalid) parts.push(`${data.skipped_invalid} invalid rows skipped`);
      toast.success(parts.length ? parts.join(" · ") : "Nothing to import");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "name,email,phone,company,address,notes\nJane Doe,jane@example.com,555-1234,Doe Studios,123 Main St,Prefers black acrylic\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Shop details"
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={save} disabled={saving} data-testid="save-settings-btn">
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface p-6">
          <div className="overline mb-4">Company Info</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Company Name</Label>
              <Input data-testid="settings-name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Phone</Label>
              <Input data-testid="settings-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Email</Label>
              <Input data-testid="settings-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Address</Label>
              <Textarea rows={2} data-testid="settings-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Default Tax Rate (%)</Label>
              <Input type="number" step="0.01" min="0" data-testid="settings-tax" value={form.default_tax_rate ?? 0} onChange={(e) => setForm({ ...form, default_tax_rate: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">From Email (for invoice emails)</Label>
              <Input
                data-testid="settings-from-email"
                type="email"
                value={form.from_email || ""}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                placeholder="orders@shop.eple1.com"
              />
              <p className="text-xs text-slate-400 mt-1">
                Must be an address on a domain you&apos;ve verified in Resend. Leave blank to use the default (<span className="font-mono">onboarding@resend.dev</span>, test mode only).
              </p>
            </div>
          </div>
        </div>

        <div className="surface p-6">
          <div className="overline mb-4">Invoice Numbering</div>
          <Label className="text-xs text-slate-600">Next Invoice Number</Label>
          <Input
            type="number"
            min="0"
            step="1"
            data-testid="settings-invoice-start"
            value={form.invoice_start_number ?? 0}
            onChange={(e) => setForm({ ...form, invoice_start_number: e.target.value })}
            placeholder="0 = auto"
            className="font-mono"
          />
          <p className="text-xs text-slate-500 mt-2">
            Set the number the <span className="font-mono">next</span> new invoice should start at. Leave <span className="font-mono">0</span> to keep the automatic sequence going. The paired work order always uses the same number as its invoice (e.g. <span className="font-mono">INV-2026-1000</span> ↔ <span className="font-mono">WO-2026-1000</span>).
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Only bumps the counter forward — cannot renumber invoices already created or skip backwards.
          </p>
        </div>

        <div className="surface p-6">
          <div className="overline mb-4 flex items-center gap-2"><UsersThree size={14} /> Import Customers</div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Upload a CSV with columns: <span className="font-mono text-slate-800">name</span>, <span className="font-mono text-slate-800">email</span>, <span className="font-mono text-slate-800">phone</span>, <span className="font-mono text-slate-800">company</span>, <span className="font-mono text-slate-800">address</span>, <span className="font-mono text-slate-800">notes</span>. Duplicates (same name + email) are skipped.
          </p>
          <div className="flex flex-col gap-2 mt-4">
            <label className="btn-primary cursor-pointer text-sm text-center" data-testid="settings-import-customers-label">
              {importing ? "Importing…" : "Choose CSV file"}
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="settings-import-customers"
                disabled={importing}
                onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])}
              />
            </label>
            <button
              type="button"
              className="btn-ghost text-sm flex items-center justify-center gap-2"
              onClick={downloadTemplate}
              data-testid="settings-import-template"
            >
              <DownloadSimple size={14} /> Download CSV template
            </button>
          </div>
          {importResult && (
            <div className="mt-4 text-xs space-y-1" data-testid="settings-import-result">
              <div className="text-emerald-700 font-semibold">Added: <span className="font-mono">{importResult.inserted}</span></div>
              <div className="text-slate-600">Duplicates skipped: <span className="font-mono">{importResult.skipped_duplicates}</span></div>
              <div className="text-slate-600">Invalid rows skipped: <span className="font-mono">{importResult.skipped_invalid}</span></div>
              {importResult.errors?.length > 0 && (
                <div className="text-red-500">
                  {importResult.errors.length} row errors — first: row {importResult.errors[0].row}: {importResult.errors[0].error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="surface p-6">
          <div className="overline mb-4">Company Logo</div>
          <div className="border border-dashed hairline p-4 flex flex-col items-center gap-3">
            {form.logo_path ? (
              <img src={`${API_BASE}/files/${form.logo_path}`} alt="Logo" className="max-h-24" data-testid="settings-logo-preview" />
            ) : (
              <div className="text-sm text-slate-400">No logo uploaded.</div>
            )}
            <label className="btn-ghost cursor-pointer text-sm">
              {uploading ? "Uploading…" : (form.logo_path ? "Replace Logo" : "Upload Logo")}
              <input type="file" accept="image/*" className="hidden" data-testid="settings-logo-upload" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-3">Logo appears on work order and invoice PDFs. PNG with transparent background works best.</p>
        </div>
      </div>
    </div>
  );
}
